import AWSAppSyncClient, { AUTH_TYPE } from "aws-appsync";
import { Auth } from "aws-amplify";
import { GraphqlClient, MemoisedSchemaClient, SchemaClient } from "@digitallab/grid-common-lib";

/**
 * Read client the equipment schema resolves its master data through.
 *
 * Points at the **instrument repository** API. This app talks to several AppSync endpoints, and the
 * equipment master data lives on that one: every source the schema reads (`listIRCategoryMappings`,
 * `listSupportedLinkedInstances`, `listIRFilterLookups`, `listIRGroupMasters`, `listIRSOPMasters`)
 * is served there. Logbook's own endpoint (`REACT_APP_AWS_GRAPHQL_ENDPOINT`) and the landing-page
 * one do not expose them — pointing at either fails every read, which leaves the derived column
 * data empty and every schema-only column missing from the grid.
 *
 * `cache-first` because every caller wants the same master data and reads it repeatedly;
 * `MemoisedSchemaClient` on top guarantees one read per source per client regardless of the Apollo
 * cache policy, since several fields resolve their filter values from the same query.
 *
 * Deliberately in its own module rather than beside the loader that uses it: `constants.tsx` imports
 * that loader for its synchronous getters, and `constants.tsx` is reachable from most of the test
 * suite. Keeping the AppSync client behind this boundary is what lets jest replace it wholesale
 * through the `moduleNameMapper` entry in `package.json`, instead of every one of those tests having
 * to resolve `aws-appsync` and the `@aws-sdk` packages behind it.
 *
 * The endpoint is read straight from the environment rather than through `constants.tsx`'s
 * `INSTRUMENTS_REPO_ENDPOINT`, which holds the same value. `constants.tsx` imports the loader that
 * imports this module, so taking the constant from there closes a cycle — and a cycle here leaves
 * `constants.tsx` half-initialised for whichever module happens to load first, which shows up as
 * its exports being `undefined` at call time rather than as an import error.
 *
 * @returns A memoised, repository-backed schema client.
 */
export const makeSchemaClient = (): SchemaClient =>
  new MemoisedSchemaClient(
    new GraphqlClient(
      new AWSAppSyncClient({
        url: process.env.REACT_APP_REPO_AWS_GRAPHQL_ENDPOINT ?? "",
        region: process.env.REACT_APP_AWS_PROJECT_REGION ?? "eu-central-1",
        auth: {
          type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
          jwtToken: async () => (await Auth.currentSession()).getAccessToken().getJwtToken()
        },
        disableOffline: true
      }),
      "cache-first"
    )
  );
